import { cn } from '@/lib/utils';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { UNDERWRITING_STATUS_CONFIG, FundingPolicyType } from '@shared';
import { Button, Spinner } from '@design-system';
import { ChevronRight } from 'lucide-react';
import { UnderwritingSlideOut } from './underwriting-slide-out';
import { QuestionnaireDialog } from '@/features/client-quick-plan/components/questionnaire-dialog';
import { useUnderwritingStatus } from '../hooks/use-underwriting-status';

interface UnderwritingStatusBadgeProps {
  productName: string;
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
  className?: string;
  size?: 'sm' | 'md'; // sm for compact badges (like in FundingTable), md for quote cards (default)
}

export function UnderwritingStatus({
  productName,
  fundingPolicyType,
  policyType,
  className,
  size = 'md',
}: UnderwritingStatusBadgeProps) {
  const { flags } = useFeatureFlags();

  const {
    status,
    underwritingResult,
    structuredResult,
    pipelineMetadata,
    hasUnderwritingResult,
    isLoading,
    showSlideOut,
    setShowSlideOut,
    showQuestionnaireDialog,
    setShowQuestionnaireDialog,
    questionnaireVariant,
    questionnaireClient,
    handleCheckEligibilityClick,
    handleViewDetails,
  } = useUnderwritingStatus({
    productName,
    fundingPolicyType,
    policyType,
  });

  if (flags.nycLifeDemo) return null;

  // Size-specific classes
  const sizeClasses =
    size === 'sm'
      ? '!h-7 w-max' // Compact size for FundingTable rows
      : 'w-28 py-1'; // Default size for quote cards

  // Show CTA button when status doesn't exist
  if (!status) {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCheckEligibilityClick}
          disabled={isLoading}
          className={cn(
            'border-primary-300 bg-primary-50 text-primary-dark hover:bg-primary-100 !h-7 w-fit self-start !rounded-full border !px-2 text-xs font-medium',
            className,
          )}
          data-testid="get-underwriting-eligibility-cta"
        >
          {isLoading ? (
            <>
              <Spinner className="h-3 w-3" />
              Checking...
            </>
          ) : (
            <>
              Check Eligibility
              <ChevronRight className="!size-3" />
            </>
          )}
        </Button>

        {/* Questionnaire Dialog */}
        <QuestionnaireDialog
          open={showQuestionnaireDialog}
          onOpenChange={setShowQuestionnaireDialog}
          variant={questionnaireVariant}
          client={questionnaireClient}
        />
      </>
    );
  }

  const config = UNDERWRITING_STATUS_CONFIG[status];
  if (!config) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleViewDetails}
        disabled={!hasUnderwritingResult}
        data-testid="quote-detail-eligibility-status"
        className={cn(
          'inline-flex h-7 w-fit items-center gap-1.5 rounded-full border px-2 text-xs font-medium transition-all',
          'hover:shadow-sm disabled:pointer-events-none disabled:opacity-50',
          hasUnderwritingResult && 'cursor-pointer',
          config.classes.bgColor,
          config.classes.textColor,
          config.classes.borderColor,
          config.classes.hoverBgColor,
          className,
        )}
        aria-label={`View ${config.label} details`}
      >
        {config.label}
        <ChevronRight className="h-3 w-3" />
      </button>

      {/* Slideout for viewing underwriting details */}
      <UnderwritingSlideOut
        open={showSlideOut}
        onClose={() => setShowSlideOut(false)}
        underwritingResult={underwritingResult}
        structuredResult={structuredResult}
        pipelineMetadata={pipelineMetadata}
        productName={productName}
      />
    </>
  );
}
